import React, { useState, useEffect, useCallback, useRef } from "react";
import "./css/MainCommon.css";
import "./css/LandingPage-Education.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faChalkboardTeacher,
  faNewspaper,
  faFish,
} from "@fortawesome/free-solid-svg-icons";

const AUTOPLAY_INTERVAL = 6000; // 6 seconds per slide

const slides = [
  {
    image: "/TrainingCenter.png",
    alt: "KinShield Training Center with interactive scam recognition courses and quizzes",
    title: "Training Center",
    description:
      "Master scam detection through interactive courses, real-world case studies, and knowledge-reinforcing quizzes tailored to your skill level.",
    icon: faChalkboardTeacher,
  },
  {
    image: "/AIH.png",
    alt: "KinShield Alerts Intelligence Hub showing curated fraud alerts from trusted sources",
    title: "Alerts Intelligence Hub",
    description:
      "Stay ahead of emerging threats with curated alerts from the FTC, FBI, BBB, and other authoritative sources — all in one place.",
    icon: faNewspaper,
  },
  {
    image: "/Simulation.png",
    alt: "KinShield safe phishing simulation training exercise",
    title: "Safe Phishing Simulations",
    description:
      "Build real-world scam recognition skills through realistic but completely safe phishing simulations with instant feedback and scoring.",
    icon: faFish,
  },
];

function LandingPageEducation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState("next");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoPlayRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const goToSlide = useCallback(
    (index, dir = "next") => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setDirection(dir);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 700);
    },
    [isTransitioning],
  );

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % slides.length;
    goToSlide(next, "next");
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    goToSlide(prev, "prev");
  }, [currentSlide, goToSlide]);

  // Autoplay
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(nextSlide, AUTOPLAY_INTERVAL);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, nextSlide]);

  // Pause autoplay on hover, resume on leave
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // Touch/swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    },
    [prevSlide, nextSlide],
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
  };

  return (
    <section className="education-section" aria-label="KinShield Education">
      <div className="scroll-to-top-link" onClick={scrollToTop}>
        Top ↑
      </div>

      {/* Section Header */}
      <div className="education-header">
        <h2 className="education-title">
          <FontAwesomeIcon
            icon={faGraduationCap}
            className="education-title-icon"
          />
          Learn to Outsmart Scammers
        </h2>
        <p className="education-subtitle">
          KinShield goes beyond detection — build lasting scam-recognition
          skills through interactive training, intelligence alerts, and
          realistic simulations.
        </p>
      </div>

      {/* Carousel */}
      <div
        className="education-carousel"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="KinShield educational features"
      >
        {/* Previous Arrow */}
        <button
          className="edu-carousel-arrow edu-carousel-arrow-left"
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        {/* Slide Container */}
        <div className="edu-carousel-viewport">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`edu-carousel-slide ${
                index === currentSlide ? "active" : ""
              } ${
                index === currentSlide
                  ? `edu-slide-enter-${direction}`
                  : "edu-slide-hidden"
              }`}
              role="tabpanel"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${slides.length}: ${slide.title}`}
              aria-hidden={index !== currentSlide}
            >
              <div className="edu-slide-content">
                <div className="edu-slide-image-container">
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="edu-slide-image"
                    loading={index === 0 ? "eager" : "lazy"}
                    draggable="false"
                  />
                </div>
                <div className="edu-slide-info">
                  <div className="edu-slide-info-icon">
                    <FontAwesomeIcon icon={slide.icon} />
                  </div>
                  <h3 className="edu-slide-title">{slide.title}</h3>
                  <p className="edu-slide-description">{slide.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Next Arrow */}
        <button
          className="edu-carousel-arrow edu-carousel-arrow-right"
          onClick={nextSlide}
          aria-label="Next slide"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>

        {/* Dot Indicators */}
        <div
          className="edu-carousel-dots"
          role="tablist"
          aria-label="Slide indicators"
        >
          {slides.map((slide, index) => (
            <button
              key={index}
              className={`edu-carousel-dot ${index === currentSlide ? "active" : ""}`}
              onClick={() =>
                goToSlide(index, index > currentSlide ? "next" : "prev")
              }
              role="tab"
              aria-selected={index === currentSlide}
              aria-label={`Go to slide ${index + 1}: ${slide.title}`}
            >
              <FontAwesomeIcon icon={faCircle} />
            </button>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="edu-carousel-progress">
          <div
            className="edu-carousel-progress-bar"
            style={{
              animationDuration: `${AUTOPLAY_INTERVAL}ms`,
              animationPlayState: isAutoPlaying ? "running" : "paused",
            }}
            key={currentSlide}
          />
        </div>
      </div>
    </section>
  );
}

export default LandingPageEducation;
